import type {
  BatteryLightReachPriceCapDto,
  BatteryProposalFinancingDto,
  BatteryQuoteDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryLightReachOffers from "~/api/hooks/useBatteryLightReachOffers"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useSaveBatteryLightReachOffer from "~/api/hooks/useSaveBatteryLightReachOffer"

import type { LightReachOfferPickerBundle } from "../../ProposalSetup/financing/lightreach/lightreach-kind-bundle"
import { LightReachOfferPicker as SharedLightReachOfferPicker } from "../../ProposalSetup/financing/lightreach/LightReachOfferPicker"
import { FinancingLockNotice } from "../financing-status/concert/FinancingLockNotice"
import { LIGHTREACH_APPLICATION_EXISTS_REASON } from "../financing-status/lightreach/lightreach-battery-copy"

export {
  LIGHTREACH_FIRST_YEAR_LABEL,
  LIGHTREACH_OFFER_REQUIRED_PROMPT,
  LIGHTREACH_OFFERS_LOAD_ERROR_PROMPT,
  LIGHTREACH_OFFERS_LOADING_LABEL,
  LIGHTREACH_PAYMENT_OUT_OF_DATE_LABEL,
  LIGHTREACH_PRICE_CAP_PILL,
  LIGHTREACH_PROVIDER_FAILED_PROMPT,
  LIGHTREACH_SELECTED_OFFER_UNAVAILABLE_PROMPT,
  LIGHTREACH_STALE_OFFER_PROMPT,
  matchLightReachOffer,
  REFRESH_LIGHTREACH_OFFER_BUSY_LABEL,
  REFRESH_LIGHTREACH_OFFER_LABEL,
  SAVE_LIGHTREACH_OFFER_BUSY_LABEL,
  SAVE_LIGHTREACH_OFFER_ERROR_PROMPT,
  SAVE_LIGHTREACH_OFFER_LABEL,
  SAVED_LIGHTREACH_OFFER_LABEL,
  SAVED_LIGHTREACH_OFFER_WITHOUT_PAYMENT_LABEL,
} from "../../ProposalSetup/financing/lightreach/LightReachOfferPicker"

export const LIGHTREACH_PRICING_REQUIRED_PROMPT = `LightReach offers need current Battery pricing and a saved Utility. Refresh Pricing, or save the Utility first.`
export const LIGHTREACH_UNAVAILABLE_PROMPT = `LightReach offers are not available for this Battery-Only Proposal.`
export const LIGHTREACH_EMPTY_PROMPT = `LightReach returned no Energy Backup offers for this price.`
export const LIGHTREACH_EXISTING_BATTERY_PROMPT = `LightReach Energy Backup is not available for homes that already have a battery.`
export const LIGHTREACH_READ_ONLY_PROMPT = `You cannot change the LightReach offer for this Battery-Only Proposal.`

const usd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** "You can still save" only while the seller can: a locked offer cannot be saved. */
export const lightReachPriceCapWarning = (
  cap: BatteryLightReachPriceCapDto,
  { canSave = true }: { canSave?: boolean } = {},
): string =>
  `This battery's price, ${usd.format(cap.pricedTotal)}, is above LightReach's ${usd.format(cap.maxPrice)} cap for ${cap.manufacturer}. LightReach may reprice the account.${canSave ? ` You can still save an offer.` : ``}`

/**
 * Battery-Only Proposal reads and commands for the shared LightReach offer
 * picker: LightReach Energy Backup offers, whose save reprices and answers
 * with the financing read and its Quote.
 */
const batteryLightReachOfferBundle: LightReachOfferPickerBundle<BatteryProposalFinancingDto> =
  {
    useOffers: useBatteryLightReachOffers,
    useSave: useSaveBatteryLightReachOffer,
    savedOutcome: (financing) =>
      financing.quote?.status === `current` &&
      financing.quote.estimatedMonthlyPayment != null
        ? `quoted`
        : `saved_only`,
    useRefreshPricing: useCalculateBatteryProposalPricing,
    LockNotice: FinancingLockNotice,
    copy: {
      pricingRequired: LIGHTREACH_PRICING_REQUIRED_PROMPT,
      unavailable: LIGHTREACH_UNAVAILABLE_PROMPT,
      empty: LIGHTREACH_EMPTY_PROMPT,
      readOnly: LIGHTREACH_READ_ONLY_PROMPT,
      kindStatusPrompts: {
        existing_battery: LIGHTREACH_EXISTING_BATTERY_PROMPT,
      },
      applicationExists: LIGHTREACH_APPLICATION_EXISTS_REASON,
      priceCapWarning: lightReachPriceCapWarning,
    },
  }

/** LightReach (Battery Only)'s half of the Loan Product drawer. */
export const LightReachOfferPicker = ({
  proposalId,
  quote,
  financing,
  onClose,
}: {
  proposalId: string
  quote: BatteryQuoteDto | null
  /** The financing read the drawer already holds; it states the lock. */
  financing: BatteryProposalFinancingDto | null
  onClose: () => void
}): JSX.Element => (
  <SharedLightReachOfferPicker
    bundle={batteryLightReachOfferBundle}
    proposalId={proposalId}
    quote={quote}
    financing={financing}
    onClose={onClose}
  />
)
